/**
 * AdBreaksTab - Admin panel for managing ad break & holding screen links.
 * Links are standalone toggles: hit once = start, hit again = end. Not tied to specific games.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Timer, Zap, Link2, Copy, Check, Clock, Film, Image as ImageIcon,
  Upload, Trash2, ExternalLink, Radio, Loader2, Play, Pause, History, Plus, X,
  MousePointerClick, MonitorPlay, LinkIcon, Power
} from 'lucide-react';
import { Button } from '../ui/button';
import { API, authHeaders } from './utils';

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="p-1.5 text-zinc-500 hover:text-white transition-colors" title="Copy URL">
      {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

export const AdBreaksTab = () => {
  const [links, setLinks] = useState([]);
  const [allAds, setAllAds] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('links');
  const [creating, setCreating] = useState(false);
  const fileRef = useRef(null);

  // New link form
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('ad_break');
  const [newDuration, setNewDuration] = useState(30);

  // Billboard form
  const [bbName, setBbName] = useState('');
  const [bbClickUrl, setBbClickUrl] = useState('');
  const [bbSponsor, setBbSponsor] = useState('');
  const [bbMode, setBbMode] = useState('upload');
  const [bbImageUrl, setBbImageUrl] = useState('');
  const [uploadingBillboard, setUploadingBillboard] = useState(false);
  const [addingBbUrl, setAddingBbUrl] = useState(false);

  // Holding screen
  const [holdingScreen, setHoldingScreen] = useState(null);
  const [hsMediaType, setHsMediaType] = useState('image');
  const [hsUrl, setHsUrl] = useState('');
  const [hsMode, setHsMode] = useState('url');
  const [savingHs, setSavingHs] = useState(false);
  const hsFileRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = authHeaders();
      const [linksRes, adsRes, histRes, hsRes] = await Promise.all([
        fetch(`${API}/ad-breaks/admin/links`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/video-ads`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/ad-breaks/admin/history?limit=30`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/video-ads/settings/holding-screen`, { headers }).then(r => r.ok ? r.json() : null)
      ]);
      setLinks(linksRes);
      setAllAds(adsRes);
      setHistory(histRes);
      if (hsRes) {
        setHoldingScreen(hsRes);
        setHsMediaType(hsRes.media_type || 'image');
        setHsUrl(hsRes.image_url || hsRes.video_url || '');
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createLink = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await fetch(`${API}/ad-breaks/admin/links`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), type: newType, duration: newDuration })
      });
      setNewName('');
      setNewDuration(30);
      fetchData();
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const deleteLink = async (linkId) => {
    if (!window.confirm('Delete this link?')) return;
    try {
      await fetch(`${API}/ad-breaks/admin/links/${linkId}`, { method: 'DELETE', headers: authHeaders() });
      fetchData();
    } catch {}
  };

  const handleBillboardUpload = async () => {
    if (!fileRef.current?.files?.[0] || !bbName) return;
    setUploadingBillboard(true);
    try {
      const formData = new FormData();
      formData.append('file', fileRef.current.files[0]);
      formData.append('name', bbName);
      formData.append('click_url', bbClickUrl);
      formData.append('sponsor_name', bbSponsor);
      await fetch(`${API}/video-ads/upload-billboard`, {
        method: 'POST', headers: { 'Authorization': authHeaders()['Authorization'] },
        body: formData
      });
      setBbName(''); setBbClickUrl(''); setBbSponsor('');
      if (fileRef.current) fileRef.current.value = '';
      fetchData();
    } catch (err) { console.error(err); }
    finally { setUploadingBillboard(false); }
  };

  const handleBillboardUrl = async () => {
    if (!bbName || !bbImageUrl) return;
    setAddingBbUrl(true);
    try {
      await fetch(`${API}/video-ads/billboard-url`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: bbName, image_url: bbImageUrl, click_url: bbClickUrl, sponsor_name: bbSponsor })
      });
      setBbName(''); setBbImageUrl(''); setBbClickUrl(''); setBbSponsor('');
      fetchData();
    } catch (err) { console.error(err); }
    finally { setAddingBbUrl(false); }
  };

  const saveHoldingScreen = async () => {
    if (hsMode === 'upload' && hsFileRef.current?.files?.[0]) {
      setSavingHs(true);
      try {
        const formData = new FormData();
        formData.append('file', hsFileRef.current.files[0]);
        formData.append('media_type', hsMediaType);
        await fetch(`${API}/video-ads/settings/holding-screen/upload`, {
          method: 'POST', headers: { 'Authorization': authHeaders()['Authorization'] },
          body: formData
        });
        if (hsFileRef.current) hsFileRef.current.value = '';
        fetchData();
      } catch (err) { console.error(err); }
      finally { setSavingHs(false); }
    } else if (hsMode === 'url' && hsUrl) {
      setSavingHs(true);
      try {
        await fetch(`${API}/video-ads/settings/holding-screen`, {
          method: 'PUT',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ media_type: hsMediaType, url: hsUrl, source: 'url' })
        });
        fetchData();
      } catch (err) { console.error(err); }
      finally { setSavingHs(false); }
    }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm('Delete this ad?')) return;
    try {
      await fetch(`${API}/video-ads/${adId}`, { method: 'DELETE', headers: authHeaders() });
      fetchData();
    } catch {}
  };

  const toggleAd = async (adId) => {
    try {
      await fetch(`${API}/video-ads/${adId}/toggle`, { method: 'POST', headers: authHeaders() });
      fetchData();
    } catch {}
  };

  const videoAds = allAds.filter(a => a.ad_type !== 'billboard');
  const billboards = allAds.filter(a => a.ad_type === 'billboard');
  const adBreakLinks = links.filter(l => l.type === 'ad_break');
  const holdingLinks = links.filter(l => l.type === 'holding_screen');

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>;
  }

  const tabs = [
    { id: 'links', label: 'Trigger Links', icon: Link2 },
    { id: 'ads', label: 'Video Ads', icon: Film },
    { id: 'billboards', label: 'Billboards', icon: ImageIcon },
    { id: 'holding', label: 'Holding Screen', icon: MonitorPlay },
    { id: 'history', label: 'History', icon: History }
  ];

  const LinkCard = ({ link }) => (
    <div className={`bg-zinc-900/50 border rounded-lg p-4 ${
      link.is_active ? 'border-green-500/30 bg-green-500/5' : 'border-white/5'
    }`} data-testid={`link-${link.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {link.is_active && (
              <span className="flex items-center gap-1 text-green-400 text-[10px] font-bold animate-pulse">
                <Power className="w-3 h-3" /> ACTIVE
              </span>
            )}
            <span className="text-white text-sm font-bold truncate">{link.name}</span>
            <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
              link.type === 'holding_screen'
                ? 'bg-blue-500/20 text-blue-400'
                : 'bg-orange-500/20 text-orange-400'
            }`}>
              {link.type === 'holding_screen' ? 'Holding' : 'Ad Break'}
            </span>
            <span className="text-zinc-500 text-[10px]">
              {link.type === 'holding_screen' ? 'Until toggled off' : `${link.duration}s`}
            </span>
          </div>

          <div className="bg-black/40 rounded px-3 py-2 font-mono text-[11px] text-zinc-400 break-all select-all flex items-center gap-2">
            <span className="flex-1 truncate" data-testid="link-ws-url">{link.url}</span>
            <CopyButton text={link.url} />
          </div>
          {link.http_url && (
            <div className="bg-black/30 rounded px-3 py-1.5 font-mono text-[10px] text-zinc-500 break-all select-all flex items-center gap-2 mt-1">
              <span className="text-zinc-600 shrink-0">HTTP:</span>
              <span className="flex-1 truncate" data-testid="link-http-url">{link.http_url}</span>
              <CopyButton text={link.http_url} />
            </div>
          )}

          <div className="flex items-center gap-3 mt-2 text-[10px] text-zinc-600">
            <span>Triggered {link.trigger_count || 0} time{(link.trigger_count || 0) !== 1 ? 's' : ''}</span>
            {link.last_toggled && <span>Last: {new Date(link.last_toggled).toLocaleString()}</span>}
          </div>
        </div>
        <button onClick={() => deleteLink(link.id)} className="p-1.5 text-zinc-600 hover:text-red-400 transition-colors shrink-0" title="Delete link">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <p className="text-zinc-600 text-[10px] mt-2">
        Use the WebSocket URL (wss://) with Stream Deck or similar tools. The HTTP URL works in browsers.
      </p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ad-breaks-tab">
      <div>
        <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">Ad Break System</h2>
        <p className="text-zinc-500 text-sm">Create toggle links for ad breaks and holding screens. Not tied to specific games.</p>
      </div>

      <div className="flex gap-1 border-b border-white/10 overflow-x-auto scrollbar-hide">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              activeTab === t.id ? 'text-white border-orange-500' : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`} data-testid={`adbreak-tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* ===== TRIGGER LINKS TAB ===== */}
      {activeTab === 'links' && (
        <div className="space-y-6">
          {/* Create new link form */}
          <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-5">
            <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4 text-orange-400" /> Create New Link
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Name *</label>
                <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. Halftime Break"
                  className="w-full bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded"
                  data-testid="link-name-input" />
              </div>
              <div>
                <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Type</label>
                <select value={newType} onChange={e => setNewType(e.target.value)}
                  className="w-full bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded"
                  data-testid="link-type-select">
                  <option value="ad_break">Ad Break</option>
                  <option value="holding_screen">Holding Screen</option>
                </select>
              </div>
              {newType === 'ad_break' && (
                <div>
                  <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Duration (sec)</label>
                  <input type="number" min={10} max={600} value={newDuration}
                    onChange={e => setNewDuration(parseInt(e.target.value) || 30)}
                    className="w-full bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded"
                    data-testid="link-duration-input" />
                </div>
              )}
              {newType === 'holding_screen' && (
                <div className="flex items-end">
                  <p className="text-zinc-500 text-xs py-2">Stays active until toggled off</p>
                </div>
              )}
            </div>
            <Button onClick={createLink} disabled={creating || !newName.trim()}
              className="bg-orange-500 hover:bg-orange-600 mt-3 text-sm" data-testid="create-link-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
              Create Link
            </Button>
          </div>

          {/* Ad Break Links */}
          {adBreakLinks.length > 0 && (
            <div>
              <h3 className="text-zinc-400 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 text-orange-400" /> Ad Break Links ({adBreakLinks.length})
              </h3>
              <div className="space-y-3">
                {adBreakLinks.map(link => <LinkCard key={link.id} link={link} />)}
              </div>
            </div>
          )}

          {/* Holding Screen Links */}
          {holdingLinks.length > 0 && (
            <div>
              <h3 className="text-zinc-400 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <MonitorPlay className="w-3.5 h-3.5 text-blue-400" /> Holding Screen Links ({holdingLinks.length})
              </h3>
              <div className="space-y-3">
                {holdingLinks.map(link => <LinkCard key={link.id} link={link} />)}
              </div>
            </div>
          )}

          {links.length === 0 && (
            <div className="bg-zinc-900/30 border border-white/5 rounded-lg p-8 text-center">
              <Link2 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400">No trigger links created yet</p>
              <p className="text-zinc-600 text-xs mt-1">Create your first link above to get started</p>
            </div>
          )}
        </div>
      )}

      {/* ===== VIDEO ADS TAB ===== */}
      {activeTab === 'ads' && (
        <div className="space-y-4">
          <p className="text-zinc-400 text-sm">{videoAds.length} video ad{videoAds.length !== 1 ? 's' : ''}</p>
          {videoAds.length === 0 ? (
            <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-8 text-center">
              <Film className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400">No video ads configured</p>
            </div>
          ) : (
            <div className="space-y-2">
              {videoAds.map(ad => (
                <div key={ad.id} className="flex items-center gap-4 p-3 bg-zinc-900/50 border border-white/5 rounded-lg group">
                  <Film className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-medium truncate">{ad.name}</span>
                      <span className={`px-1.5 py-0.5 text-[10px] rounded ${ad.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                        {ad.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="px-1.5 py-0.5 text-[10px] rounded bg-zinc-800 text-zinc-500 capitalize">{ad.ad_type?.replace('_', ' ')}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-zinc-500 mt-0.5">
                      <span>{ad.duration_seconds}s</span>
                      {ad.click_url && <span className="flex items-center gap-0.5"><ExternalLink className="w-2.5 h-2.5" /> Has link</span>}
                      {ad.sponsor_name && <span>{ad.sponsor_name}</span>}
                      <span><MousePointerClick className="w-2.5 h-2.5 inline" /> {ad.click_count || 0} clicks</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => toggleAd(ad.id)} className="p-1.5 text-zinc-500 hover:text-white" title="Toggle">
                      {ad.is_active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => deleteAd(ad.id)} className="p-1.5 text-zinc-500 hover:text-red-400" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== BILLBOARDS TAB ===== */}
      {activeTab === 'billboards' && (
        <div className="space-y-4">
          <div className="flex gap-2 mb-2">
            <button onClick={() => setBbMode('upload')}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded border transition-colors ${
                bbMode === 'upload' ? 'bg-orange-500/20 border-orange-500/30 text-orange-400' : 'bg-zinc-800 border-white/5 text-zinc-400 hover:text-white'
              }`} data-testid="bb-mode-upload">
              <Upload className="w-3 h-3" /> Upload File
            </button>
            <button onClick={() => setBbMode('url')}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded border transition-colors ${
                bbMode === 'url' ? 'bg-orange-500/20 border-orange-500/30 text-orange-400' : 'bg-zinc-800 border-white/5 text-zinc-400 hover:text-white'
              }`} data-testid="bb-mode-url">
              <LinkIcon className="w-3 h-3" /> Add via URL
            </button>
          </div>

          <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-5">
            <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
              {bbMode === 'upload' ? <Upload className="w-4 h-4 text-orange-400" /> : <LinkIcon className="w-4 h-4 text-orange-400" />}
              {bbMode === 'upload' ? 'Upload Billboard Image' : 'Add Billboard via URL'}
            </h3>
            <p className="text-zinc-500 text-xs mb-4">Full-screen billboard ads display for 15 seconds during ad breaks. Recommended: 1920x1080.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Name *</label>
                <input type="text" value={bbName} onChange={e => setBbName(e.target.value)} placeholder="Billboard name"
                  className="w-full bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded" data-testid="bb-name" />
              </div>
              <div>
                <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Sponsor Name</label>
                <input type="text" value={bbSponsor} onChange={e => setBbSponsor(e.target.value)} placeholder="Optional sponsor"
                  className="w-full bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded" data-testid="bb-sponsor" />
              </div>
              <div>
                <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Click-Through URL</label>
                <input type="url" value={bbClickUrl} onChange={e => setBbClickUrl(e.target.value)} placeholder="https://sponsor.com"
                  className="w-full bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded" data-testid="bb-click-url" />
              </div>
              {bbMode === 'upload' ? (
                <div>
                  <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Image File *</label>
                  <input ref={fileRef} type="file" accept="image/*"
                    className="w-full bg-zinc-800 border border-white/10 text-zinc-400 text-sm px-3 py-1.5 rounded file:mr-3 file:bg-zinc-700 file:text-white file:border-0 file:px-3 file:py-1 file:rounded file:text-xs" data-testid="bb-file" />
                </div>
              ) : (
                <div>
                  <label className="text-zinc-500 text-[10px] uppercase mb-1 block">Image URL *</label>
                  <input type="url" value={bbImageUrl} onChange={e => setBbImageUrl(e.target.value)} placeholder="https://example.com/billboard.jpg"
                    className="w-full bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded" data-testid="bb-image-url" />
                </div>
              )}
            </div>
            {bbMode === 'upload' ? (
              <Button onClick={handleBillboardUpload} disabled={uploadingBillboard || !bbName || !fileRef.current?.files?.[0]}
                className="bg-orange-500 hover:bg-orange-600 mt-3 text-sm" data-testid="upload-billboard-btn">
                {uploadingBillboard ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload Billboard
              </Button>
            ) : (
              <Button onClick={handleBillboardUrl} disabled={addingBbUrl || !bbName || !bbImageUrl}
                className="bg-orange-500 hover:bg-orange-600 mt-3 text-sm" data-testid="add-billboard-url-btn">
                {addingBbUrl ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Billboard
              </Button>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-zinc-400 text-sm">{billboards.length} billboard{billboards.length !== 1 ? 's' : ''}</p>
            {billboards.length === 0 ? (
              <div className="bg-zinc-900/30 border border-white/5 rounded-lg p-8 text-center">
                <ImageIcon className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500">No billboard images uploaded yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {billboards.map(bb => (
                  <div key={bb.id} className="bg-zinc-900/50 border border-white/5 rounded-lg overflow-hidden group">
                    {bb.image_url && (
                      <div className="aspect-video bg-zinc-800">
                        <img src={bb.image_url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${bb.image_url}` : bb.image_url}
                          alt={bb.name} className="w-full h-full object-contain" />
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-white text-sm font-medium truncate">{bb.name}</span>
                        <span className={`px-1.5 py-0.5 text-[10px] rounded ${bb.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                          {bb.is_active ? 'Active' : 'Off'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-500">
                        <span>15s display</span>
                        {bb.source === 'url' && <span className="flex items-center gap-0.5"><LinkIcon className="w-2.5 h-2.5" /> URL</span>}
                        {bb.click_url && <span className="flex items-center gap-0.5"><ExternalLink className="w-2.5 h-2.5" /> Link</span>}
                        {bb.sponsor_name && <span>{bb.sponsor_name}</span>}
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        <button onClick={() => toggleAd(bb.id)} className="p-1 text-zinc-500 hover:text-white text-xs">
                          {bb.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={() => deleteAd(bb.id)} className="p-1 text-red-400/60 hover:text-red-400 text-xs ml-auto">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== HOLDING SCREEN TAB ===== */}
      {activeTab === 'holding' && (
        <div className="space-y-4" data-testid="holding-screen-tab">
          <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-5">
            <h3 className="text-white font-bold text-sm mb-1 flex items-center gap-2">
              <MonitorPlay className="w-4 h-4 text-orange-400" /> Holding Screen Media
            </h3>
            <p className="text-zinc-500 text-xs mb-4">
              This media is used when a Holding Screen link is triggered, or to fill remaining time when ad content runs out during an ad break.
            </p>

            {holdingScreen && (holdingScreen.image_url || holdingScreen.video_url) && (
              <div className="mb-4 bg-zinc-800 border border-white/5 rounded-lg overflow-hidden">
                <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
                  <span className="text-zinc-400 text-xs">Current Holding Screen</span>
                  <span className="text-zinc-600 text-[10px] uppercase">{holdingScreen.media_type} &bull; {holdingScreen.source}</span>
                </div>
                <div className="aspect-video bg-black flex items-center justify-center relative">
                  {holdingScreen.media_type === 'video' && holdingScreen.video_url ? (
                    <video src={holdingScreen.video_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${holdingScreen.video_url}` : holdingScreen.video_url}
                      className="w-full h-full object-contain" controls muted />
                  ) : holdingScreen.image_url ? (
                    <img src={holdingScreen.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${holdingScreen.image_url}` : holdingScreen.image_url}
                      alt="Holding screen" className="w-full h-full object-contain" />
                  ) : null}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-black/50 backdrop-blur-sm rounded-lg px-6 py-3 text-center">
                      <p className="text-white/80 text-sm font-medium">Broadcast will return shortly</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 mb-3">
              <label className="text-zinc-500 text-[10px] uppercase">Media Type</label>
              <div className="flex gap-2">
                <button onClick={() => setHsMediaType('image')}
                  className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                    hsMediaType === 'image' ? 'bg-orange-500/20 border-orange-500/30 text-orange-400' : 'bg-zinc-800 border-white/5 text-zinc-400'
                  }`} data-testid="hs-type-image">Image</button>
                <button onClick={() => setHsMediaType('video')}
                  className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                    hsMediaType === 'video' ? 'bg-orange-500/20 border-orange-500/30 text-orange-400' : 'bg-zinc-800 border-white/5 text-zinc-400'
                  }`} data-testid="hs-type-video">Video</button>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <label className="text-zinc-500 text-[10px] uppercase">Source</label>
              <div className="flex gap-2">
                <button onClick={() => setHsMode('url')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border transition-colors ${
                    hsMode === 'url' ? 'bg-orange-500/20 border-orange-500/30 text-orange-400' : 'bg-zinc-800 border-white/5 text-zinc-400'
                  }`} data-testid="hs-mode-url">
                  <LinkIcon className="w-3 h-3" /> URL
                </button>
                <button onClick={() => setHsMode('upload')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border transition-colors ${
                    hsMode === 'upload' ? 'bg-orange-500/20 border-orange-500/30 text-orange-400' : 'bg-zinc-800 border-white/5 text-zinc-400'
                  }`} data-testid="hs-mode-upload">
                  <Upload className="w-3 h-3" /> Upload
                </button>
              </div>
            </div>

            {hsMode === 'url' ? (
              <div className="mb-3">
                <label className="text-zinc-500 text-[10px] uppercase mb-1 block">{hsMediaType === 'video' ? 'Video' : 'Image'} URL</label>
                <input type="url" value={hsUrl} onChange={e => setHsUrl(e.target.value)}
                  placeholder={hsMediaType === 'video' ? 'https://example.com/holding.mp4' : 'https://example.com/holding.jpg'}
                  className="w-full bg-zinc-800 border border-white/10 text-white text-sm px-3 py-2 rounded" data-testid="hs-url-input" />
              </div>
            ) : (
              <div className="mb-3">
                <label className="text-zinc-500 text-[10px] uppercase mb-1 block">{hsMediaType === 'video' ? 'Video' : 'Image'} File</label>
                <input ref={hsFileRef} type="file" accept={hsMediaType === 'video' ? 'video/*' : 'image/*'}
                  className="w-full bg-zinc-800 border border-white/10 text-zinc-400 text-sm px-3 py-1.5 rounded file:mr-3 file:bg-zinc-700 file:text-white file:border-0 file:px-3 file:py-1 file:rounded file:text-xs"
                  data-testid="hs-file-input" />
              </div>
            )}

            <Button onClick={saveHoldingScreen} disabled={savingHs || (hsMode === 'url' && !hsUrl)}
              className="bg-orange-500 hover:bg-orange-600 text-sm" data-testid="save-holding-screen-btn">
              {savingHs ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Save Holding Screen
            </Button>
          </div>
        </div>
      )}

      {/* ===== HISTORY TAB ===== */}
      {activeTab === 'history' && (
        <div>
          {history.length === 0 ? (
            <div className="bg-zinc-900/50 border border-white/5 rounded-lg p-8 text-center">
              <History className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400">No ad breaks triggered yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              {history.map((log, i) => (
                <div key={log.id || i} className="flex items-center gap-4 p-3 bg-zinc-900/30 border border-white/5 rounded text-xs">
                  <Timer className="w-4 h-4 text-zinc-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-zinc-300">{log.link_name || log.broadcast_title || 'Broadcast'}</span>
                    <span className="text-zinc-600 mx-2">&bull;</span>
                    <span className="text-orange-400 font-medium">{log.duration}s break</span>
                    <span className="text-zinc-600 mx-2">&bull;</span>
                    <span className="text-zinc-500">{log.ad_count || 0} ads</span>
                  </div>
                  <span className="text-zinc-600 flex-shrink-0">{log.source}</span>
                  <span className="text-zinc-600 flex-shrink-0">{new Date(log.started_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdBreaksTab;
